/** @jsx jsx */
import { jsx } from "theme-ui"
import { useState, useContext, useRef } from "react"
import PropTypes from "prop-types"
import { navigate } from "gatsby"

import ShopContext from "../context/shop-context"
import { FormName } from "./form/formContext"
import CaptchaContext from "../context/captchaContext"
import FriendlyCaptcha, { FRC_SITEKEY } from "./form/friendly-captcha"
import { FrcEnabledContext, FrcLanguageContext } from "./form/frc-context"
import {
  FRC_RESPONSE_FIELD,
  classifyFrcResponse,
  findFrcElement,
  resetFrcWidget,
  resolveFrcResponse,
} from "./form/frc"
import { ab } from "../theme"
import Button from "./button"

const Form = ({
  formName,
  heading,
  subHeading,
  successHeading,
  successMessage,
  successBtnText = "Weiter",
  redirectAfterSubmit,
  children,
  authenticationRequired = false,
  currentLanguage,
  anchorName,
  captchaRequired,
}) => {
  const { getToken } = useContext(ShopContext)
  const { getCaptcha } = useContext(CaptchaContext)

  const [submitted, setSubmitted] = useState(false)
  const formRef = useRef(null)

  // The legacy math captcha only applies while Friendly Captcha is not
  // configured — with a site key the Storyblok `captcha` blok renders the
  // Friendly Captcha widget instead (see form/captcha.js).
  const mathCaptchaActive = captchaRequired && !FRC_SITEKEY
  // Forms that require a login (shop orders) skip Friendly Captcha: the mailer
  // already checks the session.
  const frcActive = !!FRC_SITEKEY && !authenticationRequired

  const handleSubmit = async (e) => {
    e.preventDefault()

    const form = formRef.current
    const submitButton = form.querySelector('button[type="submit"]')
    submitButton.disabled = true

    if (mathCaptchaActive && getCaptcha() === false) {
      submitButton.disabled = false
      return
    }

    let authorization = {}
    if (authenticationRequired) {
      const accessToken = await getToken()
      if (!accessToken) {
        submitButton.disabled = false
        alert(
          "Your session has expired. Please log in again and resubmit the form."
        )
        return
      }
      authorization = { Authorization: `Bearer ${accessToken}` }
    }

    const formData = new FormData(e.target)
    const plainFormData = Object.fromEntries(formData.entries())

    // Friendly Captcha (DEV-735): when a site key is configured, wait (bounded)
    // for the widget to finish and forward a ready token as `frcCaptchaSolution`
    // (verified server-side by the mailer). Without a token — widget missing,
    // errored or timed out — submit without one; the mailer then rejects it
    // with 400 unless MAILER_CAPTCHA_STRICT=false on this site (SUP-477).
    const frcElement = frcActive ? findFrcElement(form) : null
    let sentFrcToken = false
    if (frcActive) {
      delete plainFormData[FRC_RESPONSE_FIELD]
      const frcResponse = await resolveFrcResponse(frcElement)
      if (classifyFrcResponse(frcResponse) === "ready") {
        plainFormData.frcCaptchaSolution = frcResponse
        sentFrcToken = true
      }
    }
    // A token is single-use: after a failed attempt, solve a fresh one.
    const resetSpentToken = () => {
      if (sentFrcToken) resetFrcWidget(frcElement)
    }

    try {
      const response = await fetch("/.netlify/functions/mailer", {
        method: "POST",
        headers: {
          accept: "application/json",
          ...authorization,
        },
        body: JSON.stringify(plainFormData),
      })

      if (response.ok) {
        setSubmitted(true)
        return
      }

      let serverMessage = ""
      try {
        const body = await response.json()
        serverMessage = body?.error || ""
      } catch {
        /* non-JSON body, fall through to generic message */
      }

      resetSpentToken()
      submitButton.disabled = false
      alert(
        serverMessage ||
          response.statusText ||
          "Submitting the form failed. Please try again or contact support@gba-group.de."
      )
    } catch {
      resetSpentToken()
      submitButton.disabled = false
      alert(
        "Network error while submitting the form. Please check your connection and try again."
      )
    }
  }

  const returnAfterSubmit = () => {
    if (redirectAfterSubmit) {
      navigate(redirectAfterSubmit)
    } else {
      setSubmitted(false)
    }
  }

  const uniqueFormName = `${formName}-${currentLanguage}`

  if (!submitted) {
    return (
      <div
        id={anchorName ? anchorName : ""}
        sx={{
          variant: "spacingContainer",
        }}
      >
        <div
          sx={{
            maxWidth: "1100px",
            variant: "guc.form",
            pr: ab({ lg: "60px" }),
            // Side-by-side layout for the legacy math captcha and the button.
            ".form-submit-button": {
              flexGrow: ab({ _: "", md: mathCaptchaActive ? 1 : "" }),
              margin: "auto 0",
              textAlign: mathCaptchaActive ? "right" : "",
            },
            // The Storyblok `captcha` blok shares a wrapping flex row with the
            // submit button; give the Friendly Captcha slot its own line so the
            // widget sits above the button.
            "& :has(> .frc-captcha[data-frc-placement='slot'])": {
              flexBasis: "100%",
            },
          }}
        >
          <div>
            <div
              sx={{
                fontSize: "34px",
                letterSpacing: 1,
              }}
            >
              {heading}
            </div>
            <div sx={{ marginBottom: 3 }}>{subHeading}</div>
          </div>

          <form
            name={uniqueFormName}
            method="POST"
            data-netlify="true"
            data-netlify-honeypot="bot-field"
            onSubmit={handleSubmit}
            ref={formRef}
          >
            <input type="hidden" name="form_name" value={uniqueFormName} />
            <input type="hidden" name="bot-field" />
            <input type="hidden" name="language" value={currentLanguage} />

            <FrcEnabledContext.Provider value={frcActive}>
              <FrcLanguageContext.Provider value={currentLanguage}>
                <FormName.Provider value={uniqueFormName}>
                  {children}
                </FormName.Provider>

                {/* Stays empty when a Storyblok `captcha` blok placed the widget. */}
                <FriendlyCaptcha />
              </FrcLanguageContext.Provider>
            </FrcEnabledContext.Provider>
          </form>
        </div>
      </div>
    )
  } else {
    return (
      <div
        sx={{
          variant: "spacingContainer",
        }}
      >
        <div
          sx={{
            maxWidth: ab({ lg: "50%" }),
            variant: "guc.successMessage",
          }}
        >
          <h1
            sx={{
              px: ab({ _: "0 !important", md: "0 !important" }),
            }}
          >
            {successHeading}
          </h1>
          {successMessage}
          <p>
            <Button onClick={returnAfterSubmit} buttonText={successBtnText} />
          </p>
        </div>
      </div>
    )
  }
}

Form.propTypes = {
  formName: PropTypes.string.isRequired,
  heading: PropTypes.string.isRequired,
  subHeading: PropTypes.string.isRequired,
  successHeading: PropTypes.string.isRequired,
  successMessage: PropTypes.node.isRequired,
  successBtnText: PropTypes.string,
  authenticationRequired: PropTypes.bool,
  currentLanguage: PropTypes.string.isRequired,
  children: PropTypes.node,
}

export default Form
